import { useState, useEffect, useCallback } from 'react';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_EXPIRY_DAYS,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from '@/constants/cookie';
import {
  deleteAnalyticsCookies,
  deleteMarketingCookies,
  getCookie,
  setCookie,
} from '@/utils/cookieUtils';

export interface CookieConsentState {
  hasConsented: boolean;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
  consentId: string;
}

function isConsentExpired(state: CookieConsentState): boolean {
  const consentDate = new Date(state.timestamp).getTime();
  const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - consentDate > expiryMs;
}

function isConsentOutdated(state: CookieConsentState): boolean {
  return !state.version || state.version !== CONSENT_VERSION;
}

function loadConsent(): CookieConsentState | null {
  try {
    // Try browser cookie first
    const cookieRaw = getCookie(CONSENT_COOKIE_NAME);
    if (cookieRaw) {
      return JSON.parse(cookieRaw) as CookieConsentState;
    }

    // Fall back to localStorage (backward compatibility)
    const storageRaw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (storageRaw) {
      const state = JSON.parse(storageRaw) as CookieConsentState;
      // Migrate to cookie
      setCookie(CONSENT_COOKIE_NAME, storageRaw, CONSENT_EXPIRY_DAYS);
      return state;
    }

    return null;
  } catch {
    return null;
  }
}

function saveConsent(state: CookieConsentState): void {
  try {
    const json = JSON.stringify(state);
    setCookie(CONSENT_COOKIE_NAME, json, CONSENT_EXPIRY_DAYS);
    localStorage.setItem(CONSENT_STORAGE_KEY, json);
  } catch {
    // ignore storage errors
  }
}

export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsentState | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    if (stored) {
      if (isConsentExpired(stored) || isConsentOutdated(stored)) {
        // Consent expired or policy changed — re-ask
        setShowBanner(true);
      } else {
        setConsentState(stored);
        setShowBanner(false);
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  // Keep multiple hook instances in sync across the app
  useEffect(() => {
    const handler = () => {
      const stored = loadConsent();
      if (stored) {
        setConsentState(stored);
        setShowBanner(false);
      }
    };
    window.addEventListener('verdea:consent-updated', handler);
    return () => window.removeEventListener('verdea:consent-updated', handler);
  }, []);

  const applyConsent = useCallback(
    (analytics: boolean, marketing: boolean) => {
      const consentId = consent?.consentId ?? crypto.randomUUID();

      const state: CookieConsentState = {
        hasConsented: true,
        necessary: true,
        analytics,
        marketing,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION,
        consentId,
      };

      if (!analytics) deleteAnalyticsCookies();
      if (!marketing) deleteMarketingCookies();

      saveConsent(state);
      setConsentState(state);
      setShowBanner(false);
      window.dispatchEvent(new CustomEvent('verdea:consent-updated'));
    },
    [consent],
  );

  const acceptAll = useCallback(() => applyConsent(true, true), [applyConsent]);
  const declineAll = useCallback(() => applyConsent(false, false), [applyConsent]);
  const saveCustom = useCallback(
    (analytics: boolean, marketing: boolean) => applyConsent(analytics, marketing),
    [applyConsent],
  );

  return { consent, showBanner, setShowBanner, acceptAll, declineAll, saveCustom };
}
